import React, { useState, useEffect } from 'react';
import { 
  Database, 
  FileSpreadsheet, 
  Server, 
  Cloud, 
  Lock, 
  Zap, 
  CheckCircle, 
  AlertCircle,
  Download,
  Play,
  Copy,
  ExternalLink,
  Globe,
  Shield,
  Layers,
  FileText,
  Table,
  Info,
  Activity,
  HardDrive,
  Upload
} from 'lucide-react';

export default function TestDataHub({ onQuickConnect, onQuickImport }) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
  const [savedConnections, setSavedConnections] = useState([]);

  const categories = [
    { id: 'all', label: 'All Test Data', icon: Layers },
    { id: 'databases', label: 'Databases', icon: Database },
    { id: 'files', label: 'Files', icon: FileSpreadsheet },
    { id: 'advanced', label: 'Advanced', icon: Shield }
  ];

  const testDataSources = [
    // PostgreSQL Databases
    {
      id: 'neon-demo-1',
      category: 'databases',
      type: 'PostgreSQL 16',
      provider: 'Neon (Serverless)',
      region: 'US West 2 (Oregon)',
      name: 'Neon Demo - Small Dataset',
      purpose: 'Beginner-friendly testing with perfect SRI schema match',
      description: '50 athletes with clean data, perfect for first-time testing and basic functionality verification',
      icon: Database,
      connection: 'Direct (SSL/TLS)',
      specs: {
        records: 50,
        tables: 1,
        latency: '~50ms',
        size: '15 KB'
      },
      config: {
        host: 'ep-soft-paper-afmx5m0c.c-2.us-west-2.aws.neon.tech',
        port: 5432,
        database: 'neondb',
        username: 'demo_user',
        password: 'demo_pass_2024',
        ssl: 'require'
      },
      features: ['Auto Schema Discovery', 'Connection Pooling', 'Pagination Support', 'AI Insights Integration'],
      whenToUse: ['First-time testing', 'Basic connection verification', 'Schema discovery demos', 'Quick proof-of-concept'],
      difficulty: 'beginner'
    },
    {
      id: 'neon-demo-3',
      category: 'databases',
      type: 'PostgreSQL 16',
      provider: 'Neon (Serverless)',
      region: 'US West 2 (Oregon)',
      name: 'Neon Demo - Schema Variations',
      purpose: 'Test fuzzy matching, unit conversion, and name parsing',
      description: '30 athletes with different column names (studentID, fullName, heightInches) to test schema mapping algorithms',
      icon: Database,
      connection: 'Direct (SSL/TLS)',
      specs: {
        records: 30,
        tables: 1,
        latency: '~60ms',
        size: '12 KB'
      },
      config: {
        host: 'ep-jolly-glade-afmn46tn.c-2.us-west-2.aws.neon.tech',
        port: 5432,
        database: 'neondb',
        username: 'demo_user',
        password: 'demo_pass_2024',
        ssl: 'require'
      },
      features: ['Fuzzy Column Matching', 'Unit Conversion (in→cm, lbs→kg)', 'Name Splitting', 'Gender Normalization'],
      whenToUse: ['Testing schema mapping', 'Verifying fuzzy matching', 'Unit conversion testing', 'Real-world schema variations'],
      difficulty: 'intermediate'
    },
    
    // MySQL Database
    {
      id: 'hetzner-mysql',
      category: 'databases',
      type: 'MySQL 8.0.44',
      provider: 'Hetzner Cloud (Self-hosted)',
      region: 'EU North (Helsinki, Finland)',
      name: 'Hetzner MySQL - External Server',
      purpose: 'Demonstrate MySQL support and external IP connections',
      description: '25 Swedish athletes on real external server, tests cross-region connectivity and MySQL-specific features',
      icon: Server,
      connection: 'Direct (External IP, No SSL)',
      specs: {
        records: 25,
        tables: 1,
        latency: '~150ms',
        ip: '65.109.9.212'
      },
      config: {
        host: '65.109.9.212',
        port: 3306,
        database: 'academy_db',
        username: 'academy_user',
        password: 'academy_pass_2024',
        ssl: 'disabled'
      },
      features: ['MySQL Engine Support', 'External IP Connection', 'Cross-Region Testing', 'AUTO_INCREMENT Support'],
      whenToUse: ['Testing MySQL connections', 'External IP connectivity', 'Cross-region performance', 'MySQL vs PostgreSQL comparison'],
      difficulty: 'intermediate'
    },

    // SSH Tunnel Connection
    {
      id: 'hetzner-ssh',
      category: 'databases',
      type: 'PostgreSQL 16',
      provider: 'Hetzner Cloud (Self-hosted)',
      region: 'EU North (Helsinki, Finland)',
      name: 'Hetzner PostgreSQL - SSH Tunnel',
      purpose: 'Demonstrate secure SSH tunnel connections (production-like setup)',
      description: '20 Finnish athletes accessed via SSH tunnel, simulates on-premises databases behind firewalls',
      icon: Shield,
      connection: 'SSH Tunnel (Key-based)',
      specs: {
        records: 20,
        tables: 1,
        latency: '~200ms',
        tunnel: 'Required'
      },
      config: {
        host: 'localhost',
        port: 5432,
        database: 'sri_test_ssh',
        username: 'sri_demo',
        password: 'sri_demo_pass_2024',
        ssl: 'disabled',
        sshHost: '65.109.9.212',
        sshPort: 22,
        sshUsername: 'root',
        sshKey: 'hetzner-dev'
      },
      features: ['SSH Tunnel Management', 'Key-based Authentication', 'Port Forwarding', 'Production Security Pattern'],
      whenToUse: ['Testing SSH tunnels', 'On-premises simulation', 'Production security demos', 'Firewall bypass scenarios'],
      difficulty: 'advanced'
    },

    // File Imports
    {
      id: 'csv-small',
      category: 'files',
      type: 'csv',
      name: 'CSV - Small Dataset',
      description: '100 athletes, clean data, perfect for testing',
      icon: FileText,
      color: 'cyan',
      stats: { rows: 100, size: '15 KB', format: 'CSV' },
      downloadUrl: '/test-data/athletes-small.csv',
      features: ['Clean Data', 'All Fields', 'No Errors'],
      difficulty: 'beginner'
    },
    {
      id: 'xlsx-medium',
      category: 'files',
      type: 'xlsx',
      name: 'Excel - Medium Dataset',
      description: '500 athletes, multiple sheets, complex structure',
      icon: FileSpreadsheet,
      color: 'green',
      stats: { rows: 500, size: '85 KB', format: 'XLSX' },
      downloadUrl: '/test-data/athletes-medium.xlsx',
      features: ['Multi-Sheet', 'Formulas', 'Formatting'],
      difficulty: 'intermediate'
    },
    {
      id: 'google-sheets',
      category: 'files',
      type: 'google-sheets',
      name: 'Google Sheets - Live Data',
      description: 'Real-time data from Google Sheets, auto-updates',
      icon: Cloud,
      color: 'blue',
      stats: { rows: 250, size: 'Live', format: 'Sheets' },
      sheetUrl: 'https://docs.google.com/spreadsheets/d/1abc123/edit',
      features: ['Live Updates', 'Collaborative', 'Cloud-Based'],
      difficulty: 'intermediate'
    },
    {
      id: 'csv-messy',
      category: 'files',
      type: 'csv',
      name: 'CSV - Messy Data (Challenge)',
      description: 'Missing fields, duplicates, wrong units - tests validation',
      icon: AlertCircle,
      color: 'yellow',
      stats: { rows: 300, size: '45 KB', format: 'CSV' },
      downloadUrl: '/test-data/athletes-messy.csv',
      features: ['Data Validation', 'Auto-Fix', 'Quality Scoring'],
      difficulty: 'advanced'
    },

    // Advanced Scenarios
    {
      id: 'multi-academy',
      category: 'advanced',
      type: 'postgresql',
      name: 'Multi-Academy Federation',
      description: 'Multiple academies in one database, tests filtering',
      icon: Layers,
      color: 'purple',
      connection: 'direct',
      stats: { athletes: '5K', academies: 10, tables: 5 },
      config: {
        host: 'ep-demo-multi.neon.tech',
        port: 5432,
        database: 'federation_db',
        username: 'demo_user',
        password: 'demo_pass_2024',
        ssl: true
      },
      features: ['Multi-Tenant', 'Complex Queries', 'Federation'],
      difficulty: 'advanced'
    },
    {
      id: 'real-time',
      category: 'advanced',
      type: 'postgresql',
      name: 'Real-Time Updates',
      description: 'Database with live updates, tests refresh & sync',
      icon: Zap,
      color: 'yellow',
      connection: 'direct',
      stats: { athletes: 1000, updates: 'Live', latency: '~80ms' },
      config: {
        host: 'ep-demo-realtime.neon.tech',
        port: 5432,
        database: 'realtime_academy',
        username: 'demo_user',
        password: 'demo_pass_2024',
        ssl: true
      },
      features: ['Real-Time', 'Auto-Refresh', 'Live Sync'],
      difficulty: 'advanced'
    }
  ];

  useEffect(() => {
    loadSavedConnections();
  }, []);

  const loadSavedConnections = async () => {
    try {
      const userId = localStorage.getItem('userId') || '00000000-0000-0000-0000-000000000000';
      const response = await fetch('/api/external-db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'list-connections',
          data: { userId }
        })
      });

      const result = await response.json();
      if (result.success) {
        setSavedConnections(result.connections || []);
      }
    } catch (error) {
      console.error('Failed to load saved connections:', error);
    }
  };

  const isConnected = (source) => {
    if (!source.config) return false;
    return savedConnections.some(conn => 
      conn.host === source.config.host && 
      conn.database_name === source.config.database &&
      conn.db_type === source.config.dbType
    );
  };

  const getConnectionId = (source) => {
    const conn = savedConnections.find(c => 
      c.host === source.config.host && 
      c.database_name === source.config.database &&
      c.db_type === source.config.dbType
    );
    return conn ? conn.id : null;
  };

  const filteredData = activeCategory === 'all' 
    ? testDataSources 
    : testDataSources.filter(d => d.category === activeCategory);

  const handleQuickConnect = (source) => {
    if (source.config) {
      onQuickConnect(source);
    }
  };

  const handleDisconnect = async (source) => {
    const connectionId = getConnectionId(source);
    if (!connectionId) return;

    if (!window.confirm(`Disconnect from ${source.name}?`)) {
      return;
    }

    try {
      const userId = localStorage.getItem('userId') || '00000000-0000-0000-0000-000000000000';
      const response = await fetch('/api/external-db', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete-connection',
          data: { connectionId, userId }
        })
      });

      const result = await response.json();
      if (result.success) {
        await loadSavedConnections();
      } else {
        alert('Failed to disconnect: ' + result.error);
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      alert('Failed to disconnect: ' + error.message);
    }
  };

  const handleDownload = (source) => {
    if (source.downloadUrl) {
      window.open(source.downloadUrl, '_blank');
    }
  };

  const handleCopyConfig = (source) => {
    const config = JSON.stringify(source.config, null, 2);
    navigator.clipboard.writeText(config);
    setCopiedId(source.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-400';
      case 'intermediate': return 'text-yellow-400';
      case 'advanced': return 'text-red-400';
      default: return 'text-slate-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 rounded-xl border border-blue-500/30 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2 flex items-center space-x-3">
              <Zap className="w-7 h-7 text-yellow-400" />
              <span>Test Data Hub</span>
            </h2>
            <p className="text-slate-300 text-sm max-w-2xl">
              Pre-configured test databases, sample files, and connection examples. 
              One-click setup to test all External DB features including schema mapping, 
              unit conversion, SSH tunnels, and data validation.
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-white">{filteredData.length}</div>
            <div className="text-xs text-slate-400">Test Sources</div>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex items-center space-x-2 overflow-x-auto pb-2">
        {categories.map(cat => {
          const Icon = cat.icon;
          const isActive = activeCategory === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                isActive 
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' 
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm font-medium">{cat.label}</span>
            </button>
          );
        })}
      </div>

      {/* Horizontal Hero Cards */}
      <div className="space-y-4">
        {filteredData.map(source => {
          const Icon = source.icon;
          
          return (
            <div
              key={source.id}
              className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 hover:border-slate-600 transition-all duration-200 overflow-hidden"
            >
              <div className="p-6">
                {/* Header Row */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-blue-600/20 rounded-lg">
                      <Icon className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{source.name}</h3>
                      <div className="flex items-center space-x-3 text-sm">
                        <span className="text-slate-400">{source.type}</span>
                        {source.provider && (
                          <>
                            <span className="text-slate-600">•</span>
                            <span className="text-slate-400">{source.provider}</span>
                          </>
                        )}
                        {source.region && (
                          <>
                            <span className="text-slate-600">•</span>
                            <span className="text-slate-400">{source.region}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(source.difficulty)} bg-slate-900/50`}>
                      {source.difficulty?.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Purpose & Description */}
                {source.purpose && (
                  <div className="mb-4 p-3 bg-blue-600/10 border border-blue-600/20 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-blue-300 mb-1">Purpose</div>
                        <div className="text-sm text-slate-300">{source.purpose}</div>
                      </div>
                    </div>
                  </div>
                )}

                <p className="text-slate-300 text-sm mb-4">{source.description}</p>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                  {/* Database Specifications */}
                  {source.specs && (
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <HardDrive className="w-4 h-4 text-slate-400" />
                        <h4 className="text-sm font-semibold text-white">Specifications</h4>
                      </div>
                      <div className="space-y-2">
                        {Object.entries(source.specs).map(([key, value]) => (
                          <div key={key} className="flex justify-between items-center">
                            <span className="text-xs text-slate-400 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                            <span className="text-sm font-medium text-white">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Connection Details */}
                  {source.config && (
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <Activity className="w-4 h-4 text-slate-400" />
                        <h4 className="text-sm font-semibold text-white">Connection</h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs text-slate-400">Type</span>
                          <span className="text-sm font-medium text-white">{source.connection}</span>
                        </div>
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs text-slate-400">Host</span>
                          <span className="text-xs font-mono text-blue-400 break-all">
                            {source.config.host}
                          </span>
                        </div>
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs text-slate-400">Port</span>
                          <span className="text-sm font-medium text-white">{source.config.port}</span>
                        </div>
                        <div className="flex flex-col space-y-1">
                          <span className="text-xs text-slate-400">Database</span>
                          <span className="text-sm font-mono text-white">{source.config.database}</span>
                        </div>
                        {source.config.username && (
                          <div className="flex flex-col space-y-1">
                            <span className="text-xs text-slate-400">Username</span>
                            <span className="text-sm font-mono text-slate-300">{source.config.username}</span>
                          </div>
                        )}
                        {source.config.ssl && (
                          <div className="flex flex-col space-y-1">
                            <span className="text-xs text-slate-400">SSL</span>
                            <span className="text-sm font-medium text-green-400">{source.config.ssl}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* When to Use */}
                  {source.whenToUse && (
                    <div className="bg-slate-900/50 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <CheckCircle className="w-4 h-4 text-slate-400" />
                        <h4 className="text-sm font-semibold text-white">When to Use</h4>
                      </div>
                      <ul className="space-y-1.5">
                        {source.whenToUse.slice(0, 4).map((use, idx) => (
                          <li key={idx} className="text-xs text-slate-300 flex items-start">
                            <span className="text-blue-400 mr-2">•</span>
                            <span>{use}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Features */}
                {source.features && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-white mb-2">Features</h4>
                    <div className="flex flex-wrap gap-2">
                      {source.features.map((feature, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-3 py-1.5 bg-blue-600/20 text-blue-300 rounded-full border border-blue-600/30"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center space-x-3 pt-4 border-t border-slate-700">
                  {source.config && (
                    <>
                      {isConnected(source) ? (
                        <button
                          onClick={() => handleDisconnect(source)}
                          className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-600/20 hover:bg-red-600 text-green-400 hover:text-white border border-green-600/30 hover:border-red-600 rounded-lg transition-colors text-sm font-medium"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>Connected - Click to Disconnect</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleQuickConnect(source)}
                          className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                        >
                          <Play className="w-4 h-4" />
                          <span>Quick Connect</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleCopyConfig(source)}
                        className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm font-medium"
                      >
                        {copiedId === source.id ? (
                          <>
                            <CheckCircle className="w-4 h-4 text-green-400" />
                            <span className="text-green-400">Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            <span>Copy Config</span>
                          </>
                        )}
                      </button>
                    </>
                  )}
                  {source.downloadUrl && (
                    <>
                      <button
                        onClick={() => onQuickImport(source)}
                        className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                      >
                        <Upload className="w-4 h-4" />
                        <span>Quick Import</span>
                      </button>
                      <button
                        onClick={() => handleDownload(source)}
                        className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm font-medium"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                    </>
                  )}
                  {source.sheetUrl && (
                    <button
                      onClick={() => window.open(source.sheetUrl, '_blank')}
                      className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>Open Sheet</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Start Guide */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center space-x-2">
          <Table className="w-5 h-5 text-blue-400" />
          <span>Quick Start Guide</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <div className="text-green-400 font-medium text-sm">1. Beginner</div>
            <p className="text-slate-300 text-xs">
              Start with "Neon Demo - Small Dataset" or "CSV - Small Dataset" for clean, simple testing.
            </p>
          </div>
          <div className="space-y-2">
            <div className="text-yellow-400 font-medium text-sm">2. Intermediate</div>
            <p className="text-slate-300 text-xs">
              Try "Schema Variations" or "Large Dataset" to test pagination, mapping, and performance.
            </p>
          </div>
          <div className="space-y-2">
            <div className="text-red-400 font-medium text-sm">3. Advanced</div>
            <p className="text-slate-300 text-xs">
              Test "SSH Tunnel", "Multi-Academy", or "Messy Data" for production-like scenarios.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
